import axios from 'axios';
import { API_ENDPOINTS } from './types';
import type {
  WorkflowTemplateDTO,
  PaginatedWorkflowTemplates,
  WorkflowTemplateUpsertRequest,
  InstantiatedUseCase,
} from './templateTypes';

/** Admin endpoints live under /admin, alongside the tenant-facing ones. */
const adminTemplatesBase = () => API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '/api/v1/admin/templates');

export interface TemplateField {
  id: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  [key: string]: any;
}

export interface TemplateDTO {
  id: number;
  templateType: string;
  templateId: string;
  title: string;
  description: string;
  category: string;
  icon?: string;
  fields: TemplateField[];
}

export interface PaginatedTemplatesResponse {
  contents: TemplateDTO[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

class TemplateApiService {
  /**
   * Fetch paginated list of templates filtered optionally by type and category
   */
  async getTemplates(params?: {
    type?: string;
    category?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedTemplatesResponse> {
    try {
      const response = await axios.get(API_ENDPOINTS.TEMPLATES, {
        params: {
          type: params?.type || 'FORM',
          category: params?.category,
          page: params?.page || 0,
          size: params?.size || 10,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  /**
   * Fetch unique list of categories for a given template type
   */
  async getTemplateCategories(type?: string): Promise<string[]> {
    try {
      const response = await axios.get(`${API_ENDPOINTS.TEMPLATES}/categories`, {
        params: {
          type: type || 'FORM',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template categories:', error);
      throw error;
    }
  }

  /**
   * Fetch paginated list of templates for admin
   */
  async getAdminTemplates(params?: {
    type?: string;
    category?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedTemplatesResponse> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      const response = await axios.get(`${apiBase}/api/v1/admin/templates`, {
        params: {
          type: params?.type || 'FORM',
          category: params?.category,
          page: params?.page || 0,
          size: params?.size || 10,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin templates:', error);
      throw error;
    }
  }

  /**
   * Fetch unique list of categories for admin
   */
  async getAdminTemplateCategories(type?: string): Promise<string[]> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      const response = await axios.get(`${apiBase}/api/v1/admin/templates/categories`, {
        params: {
          type: type || 'FORM',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin template categories:', error);
      throw error;
    }
  }

  /**
   * Delete a template by its template ID (admin only)
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      await axios.delete(`${apiBase}/api/v1/admin/templates/${templateId}`);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  /**
   * Bulk delete templates by their template IDs (admin only)
   */
  async bulkDeleteTemplates(templateIds: string[]): Promise<{ deleted: number; requested: number }> {
    try {
      const apiBase = API_ENDPOINTS.TEMPLATES.replace('/api/v1/templates', '');
      const response = await axios.post(`${apiBase}/api/v1/admin/templates/bulk-delete`, {
        templateIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk deleting templates:', error);
      throw error;
    }
  }

  // ---------------------------------------------------------------------
  // Workflow templates (template gallery)
  // ---------------------------------------------------------------------

  /**
   * Every published workflow template. Loaded once; the gallery filters client-side.
   * The response carries metadata plus a precomputed plugin summary — never definitions,
   * which run ~135KB each.
   *
   * ponytail: one call with a large page size, no pagination UI. The catalogue is curated
   * (tens of entries). Move search server-side if it ever reaches the hundreds.
   */
  async getWorkflowTemplates(): Promise<WorkflowTemplateDTO[]> {
    const response = await axios.get<PaginatedWorkflowTemplates>(API_ENDPOINTS.TEMPLATES, {
      params: { type: 'WORKFLOW', page: 0, size: 200 },
    });
    return response.data?.contents ?? [];
  }

  /**
   * Admin: one template including its definitions.
   *
   * Not the public reader: that one is tenant scoped, and a platform admin browses without a
   * tenant or a workspace, so from the admin portal it answers 400.
   */
  async getAdminWorkflowTemplate(templateId: string): Promise<WorkflowTemplateDTO> {
    const response = await axios.get<WorkflowTemplateDTO>(
      `${adminTemplatesBase()}/${encodeURIComponent(templateId)}`
    );
    return response.data;
  }

  /** One template including its workflow definition — the read instantiation uses. */
  async getWorkflowTemplate(templateId: string): Promise<WorkflowTemplateDTO> {
    const response = await axios.get<WorkflowTemplateDTO>(
      `${API_ENDPOINTS.TEMPLATES}/${encodeURIComponent(templateId)}`
    );
    return response.data;
  }

  /**
   * "Use this" — create every workflow of a use case in the caller's workspace.
   *
   * One call, one transaction: all the parts or none. The response lists them entry first, so the
   * caller navigates to `entryWorkflowId`.
   */
  async instantiateUseCase(templateId: string): Promise<InstantiatedUseCase> {
    const response = await axios.post<InstantiatedUseCase>(
      `${API_ENDPOINTS.TEMPLATES}/${encodeURIComponent(templateId)}/instantiate`
    );
    return response.data;
  }

  /** Admin: create a workflow template from an uploaded definition. */
  async createWorkflowTemplate(body: WorkflowTemplateUpsertRequest): Promise<WorkflowTemplateDTO> {
    const response = await axios.post<WorkflowTemplateDTO>(adminTemplatesBase(), body);
    return response.data;
  }

  /** Admin: show or hide a template in the gallery without touching its definition. */
  async setWorkflowTemplateStatus(
    templateId: string,
    status: 'PUBLISHED' | 'DRAFT'
  ): Promise<WorkflowTemplateDTO> {
    const response = await axios.patch<WorkflowTemplateDTO>(
      `${adminTemplatesBase()}/${encodeURIComponent(templateId)}/status`,
      { status }
    );
    return response.data;
  }

  /**
   * Admin: announce a template without letting anyone take it yet, or withdraw that.
   *
   * Separate from `setWorkflowTemplateStatus` because the two decide different things — visible,
   * and usable. A coming-soon template is still published or drafted like any other.
   */
  async setWorkflowTemplateComingSoon(
    templateId: string,
    comingSoon: boolean
  ): Promise<WorkflowTemplateDTO> {
    const response = await axios.patch<WorkflowTemplateDTO>(
      `${adminTemplatesBase()}/${encodeURIComponent(templateId)}/coming-soon`,
      { comingSoon }
    );
    return response.data;
  }

  /** Admin: update metadata (and optionally the definition) of an existing template. */
  async updateWorkflowTemplate(
    templateId: string,
    body: WorkflowTemplateUpsertRequest
  ): Promise<WorkflowTemplateDTO> {
    const response = await axios.put<WorkflowTemplateDTO>(
      `${adminTemplatesBase()}/${encodeURIComponent(templateId)}`,
      body
    );
    return response.data;
  }
}

const templateApiService = new TemplateApiService();
export default templateApiService;

export const getTemplates = templateApiService.getTemplates.bind(templateApiService);
export const getTemplateCategories = templateApiService.getTemplateCategories.bind(templateApiService);
export const getAdminTemplates = templateApiService.getAdminTemplates.bind(templateApiService);
export const getAdminTemplateCategories = templateApiService.getAdminTemplateCategories.bind(templateApiService);
export const deleteTemplate = templateApiService.deleteTemplate.bind(templateApiService);
export const bulkDeleteTemplates = templateApiService.bulkDeleteTemplates.bind(templateApiService);
export const getWorkflowTemplates = templateApiService.getWorkflowTemplates.bind(templateApiService);
export const getWorkflowTemplate = templateApiService.getWorkflowTemplate.bind(templateApiService);
export const getAdminWorkflowTemplate =
  templateApiService.getAdminWorkflowTemplate.bind(templateApiService);
export const instantiateUseCase = templateApiService.instantiateUseCase.bind(templateApiService);
export const createWorkflowTemplate = templateApiService.createWorkflowTemplate.bind(templateApiService);
export const updateWorkflowTemplate = templateApiService.updateWorkflowTemplate.bind(templateApiService);
export const setWorkflowTemplateStatus = templateApiService.setWorkflowTemplateStatus.bind(templateApiService);
export const setWorkflowTemplateComingSoon = templateApiService.setWorkflowTemplateComingSoon.bind(templateApiService);

